import { useEffect, useRef, useState } from 'react'

const PROGRESS_DURATION_MS = 300_000
const UPDATE_INTERVAL_MS = 100
const PROGRESS_INCREMENT = (100 / PROGRESS_DURATION_MS) * UPDATE_INTERVAL_MS

export interface UseFeedbackGeneratingProgressParams {
  /** When true, animates 0→100 over ~5 minutes; when false, snaps to 100% and clears the interval */
  isAnimating: boolean
}

/**
 * Determinate progress for the “feedback is generating” state (~5 minute fill).
 */
export function useFeedbackGeneratingProgress({ isAnimating }: UseFeedbackGeneratingProgressParams): {
  progress: number
} {
  const [progress, setProgress] = useState(0)
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }

    if (!isAnimating) {
      setProgress(100)
      return
    }

    setProgress(0)
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = prev + PROGRESS_INCREMENT
        return next >= 100 ? 100 : next
      })
    }, UPDATE_INTERVAL_MS)

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
    }
  }, [isAnimating])

  return { progress }
}
